import React, { useState } from 'react';
import PropTypes from 'prop-types';
// @mui
import {
  Box,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Button,
  Card,
  CardContent,
  Pagination,
  Chip,
  Alert,
  InputAdornment,
  IconButton,
  Tooltip,
  Fade,
  Switch,
  FormControlLabel
} from '@mui/material';
import { styled } from '@mui/material/styles';
// @iconify
import { Icon } from '@iconify/react';
import searchFill from '@iconify/icons-eva/search-fill';
import filterFill from '@iconify/icons-eva/funnel-fill';
import refreshFill from '@iconify/icons-eva/refresh-fill';
import addFill from '@iconify/icons-eva/plus-fill';
import downloadFill from '@iconify/icons-eva/download-fill';
import uploadFill from '@iconify/icons-eva/upload-fill';
import viewModuleFill from '@iconify/icons-eva/grid-fill';
import viewListFill from '@iconify/icons-eva/list-fill';
// components
import { TariffCard } from './index';
import { BaseTable } from '../../BaseTable/BaseTable';
import { Loader } from 'src/components/Loader';
// constants
import { tariffTableColumns } from './tariff.constants';
// hooks
import { useDropdown } from 'src/hooks/useDropdown.hook';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[2],
  '&:hover': {
    boxShadow: theme.shadows[8],
  },
}));

const FilterSection = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  marginBottom: theme.spacing(3),
}));

const TariffList = ({
  tariffs = [],
  loading,
  error,
  selectedTariffIds = [],
  searchTerm = '',
  typeFilter = 'All Types',
  partyFilter = 'All PartyId',
  pagination = { offset: 0, limit: 50, total: 0 },
  hasSelectedCPOTariffs = false,
  emptyMessage = "No tariffs found",
  onSearchChange,
  onTypeFilterChange,
  onPartyFilterChange,
  onTariffSelect,
  onTariffView,
  onTariffEdit,
  onTariffDelete,
  onSelectAll,
  onClearSelection,
  onActivateSelected,
  onDeactivateSelected,
  onDeleteSelected,
  onExportAll,
  onExportSelected,
  onImport,
  onCreateTariff,
  onPageChange,
  onPageSizeChange
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'table'
  const { data: tariffTypes } = useDropdown("tariff_type");
  const { data: partyIds } = useDropdown("ocpi_party_ids");

  const handlePageChange = (event, page) => {
    onPageChange?.(page - 1); // Convert to 0-based index
  };

  const handlePageSizeChange = (event) => {
    onPageSizeChange(parseInt(event.target.value, 10));
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  // Get table columns with action handlers
  const tableColumns = tariffTableColumns(onTariffView, onTariffEdit, onTariffDelete);

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header Section */}
      <StyledCard sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              OCPI Tariffs
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                startIcon={<Icon icon={addFill} width={20} height={20} />}
                onClick={onCreateTariff}
              >
                Create Tariff
              </Button>
            </Stack>
          </Stack>

          {/* Search and Quick Actions */}
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              placeholder="Search tariffs..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              InputProps={{
                startAdornment:  (
                  <InputAdornment position="start">
                    <Icon icon="eva:search-fill" />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1, maxWidth: 400 }}
              size="small"
            />
            
            <Tooltip title="Toggle Filters">
              <IconButton
                onClick={() => setShowFilters(!showFilters)}
                color={showFilters ? 'primary' : 'default'}
              >
                <Icon icon={filterFill} width={20} height={20} />
              </IconButton>
            </Tooltip>

            <FormControlLabel
              control={
                <Switch
                  checked={viewMode === 'table'}
                  onChange={(e) => setViewMode(e.target.checked ? 'table' : 'card')}
                  color="primary"
                />
              }
              label={
                <Tooltip title={viewMode === 'card' ? 'Switch to Table View' : 'Switch to Card View'}>
                  <Icon 
                    icon={viewMode === 'card' ? viewListFill : viewModuleFill} 
                    width={20} 
                    height={20} 
                  />
                </Tooltip>
              }
              sx={{ lineHeight: '0px' }}
              labelPlacement="end"
            />
          </Stack>

          {/* Filters Section */}
          {showFilters && (
            <FilterSection>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Type Filter</InputLabel>
                    <Select
                      value={typeFilter}
                      onChange={(e) => onTypeFilterChange(e.target.value)}
                      label="Type Filter"
                    >
                      {tariffTypes && tariffTypes.map((type) => (
                        <MenuItem key={type.id} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Party ID Filter</InputLabel>
                    <Select
                      value={partyFilter}
                      onChange={(e) => onPartyFilterChange(e.target.value)}
                      label="Party ID Filter"
                    >
                      <MenuItem value="All PartyId">All PartyId</MenuItem>
                      {partyIds && partyIds.map((partyId) => (
                        <MenuItem key={partyId.id} value={partyId.value}>{partyId.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </FilterSection>
          )}

          {/* Bulk Actions */}
          {selectedTariffIds.length > 0 && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: 'primary.lighter', borderRadius: 1 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="body2" color="primary.dark">
                  {selectedTariffIds.length} tariff(s) selected
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onClearSelection}
                >
                  Clear Selection
                </Button>
                {!hasSelectedCPOTariffs && (
                  <>
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      onClick={onActivateSelected}
                    >
                      Activate Selected
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={onDeactivateSelected}
                    >
                      Deactivate Selected
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={onDeleteSelected}
                    >
                      Delete Selected
                    </Button>
                  </>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Icon icon={downloadFill} />}
                  onClick={onExportSelected}
                >
                  Export Selected
                </Button>
              </Stack>
            </Box>
          )}

          {/* Export/Import Actions */}
          {/* <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Icon icon={downloadFill} />}
              onClick={onExportAll}
              size="small"
            >
              Export All
            </Button>
            <Button
              variant="outlined"
              startIcon={<Icon icon={uploadFill} />}
              onClick={onImport}
              size="small"
            >
              Import Tariffs
            </Button>
          </Stack> */}
        </CardContent>
      </StyledCard>

      {/* Tariff List */}
      {loading ? (
        <Loader variant='skeleton' showSkeleton={true} />
      ) : tariffs.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Icon 
            icon="eva:price-tag-outline" 
            style={{ fontSize: '64px', color: '#ccc', marginBottom: '16px' }}
          />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            {emptyMessage}
          </Typography>
          <Alert severity="info" sx={{ maxWidth: 400, mx: 'auto' }}>
            <Typography variant="body2">
              No OCPI tariffs have been created yet. Tariffs will appear here once they are configured.
            </Typography>
          </Alert>
        </Box>
      ) : viewMode === 'table' ? (
        <StyledCard>
          <CardContent>
            <BaseTable
              rows={tariffs}
              columns={tableColumns}
              getRowId={(row) => row.id || row.tariff_id}
              pageSize={pagination.limit}
              rowsPerPageOptions={[10, 25, 50, 100]}
              pagination={true}
              showExportCsvBtn={false}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              totalRows={pagination.total}
              serverRowFetcher={false}
              paginationModel={{
                page: Math.floor(pagination.offset / pagination.limit),
                pageSize: pagination.limit
              }}
              sx={{
                ".MuiDataGrid-columnHeaderTitle": {
                  overflow: "hidden !important",
                  lineHeight: "20px !important",
                  whiteSpace: "normal !important",
                },
              }}
            />
          </CardContent>
        </StyledCard>
      ) : (
        <Box>
          {/* Results Summary */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {tariffs.length} tariff{tariffs.length !== 1 ? 's' : ''}
            </Typography>
            <Chip 
              label={`${tariffs.length} total`} 
              size="small" 
              color="primary" 
              variant="outlined"
            />
          </Box>

          {/* Tariffs Grid */}
          <Grid container spacing={3}>
            {tariffs.map((tariff) => (
              <Grid item xs={12} sm={6} md={4} key={tariff.id || tariff.tariff_id}>
                <Fade in timeout={300}>
                  <Box>
                    <TariffCard
                      tariff={tariff}
                      selected={selectedTariffIds.includes(tariff.id || tariff.tariff_id)}
                      onSelect={() => onTariffSelect(tariff.id || tariff.tariff_id)}
                      onView={() => onTariffView(tariff.id)}
                      onEdit={() => onTariffEdit(tariff.id || tariff.tariff_id)}
                      onDelete={() => onTariffDelete(tariff.id || tariff.tariff_id)}
                    />
                  </Box>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Pagination for Card View */}
      {!loading && viewMode === 'card' && totalPages > 1 && (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 4 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small">
              <Select
                value={pagination.limit}
                onChange={handlePageSizeChange}
                sx={{ minWidth: 80 }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              {`${pagination.offset + 1}-${Math.min(pagination.offset + pagination.limit, pagination.total)} of ${pagination.total}`}
            </Typography>
            <Pagination
              count={totalPages}
              page={Math.floor(pagination.offset / pagination.limit) + 1}
              onChange={handlePageChange}
              color="primary"
            />
          </Stack>
        </Box>
      )}
    </Box>
  );
};

TariffList.propTypes = {
  tariffs: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.string,
  selectedTariffIds: PropTypes.array,
  searchTerm: PropTypes.string,
  typeFilter: PropTypes.string,
  statusFilter: PropTypes.string,
  pagination: PropTypes.object,
  hasSelectedCPOTariffs: PropTypes.bool,
  onSearchChange: PropTypes.func,
  onTypeFilterChange: PropTypes.func,
  onStatusFilterChange: PropTypes.func,
  onTariffSelect: PropTypes.func,
  onTariffView: PropTypes.func,
  onTariffEdit: PropTypes.func,
  onTariffDelete: PropTypes.func,
  onSelectAll: PropTypes.func,
  onClearSelection: PropTypes.func,
  onActivateSelected: PropTypes.func,
  onDeactivateSelected: PropTypes.func,
  onDeleteSelected: PropTypes.func,
  onExportAll: PropTypes.func,
  onExportSelected: PropTypes.func,
  onImport: PropTypes.func,
  onCreateTariff: PropTypes.func,
  onRefresh: PropTypes.func,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
};

export default TariffList;
